import { faBoxOpen, faEllipsis, faEye, faTruck, faUserSecret } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { usePermissions } from '@shared/hooks/usePermissions';
import { Client } from '../../models/Clients';
import { ImpersonateModal, ImpersonateModalRef } from '../modals';

interface DropdownActionsProps {
  client: Client;
  onImpersonate?: (userId: number) => void;
}

export const DropdownActions = ({ client, onImpersonate }: DropdownActionsProps) => {
  const { t } = useTranslation('clientsModule');
  const { canAccess } = usePermissions();
  const impersonateModalRef = useRef<ImpersonateModalRef>(null);

  // 🎯 Verificar permisos
  const isTenant3PL = canAccess(['tenant_3pl']);
  const canImpersonate = canAccess(['tenant_3pl'], ['iam.impersonation.start']);

  const handleImpersonateClick = () => {
    impersonateModalRef.current?.open(() => {
      if (onImpersonate) {
        onImpersonate(client.user_id);
      }
    }, client.name);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="hover:bg-muted rounded-md p-2 transition-colors"
          data-testid="dropdown-trigger"
          onClick={(e) => e.stopPropagation()}
        >
          <FontAwesomeIcon icon={faEllipsis} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-44 border-card-border" data-testid="dropdown-content">
        {/* Opción de ver detalles */}
        <DropdownMenuLinkItem
          to={`/clients/${client.id}`}
          onClick={(e) => e.stopPropagation()}
          data-testid="view-client-action"
        >
          <FontAwesomeIcon icon={faEye} className="mr-2 w-3.5" />
          {t('actions.view')}
        </DropdownMenuLinkItem>

        {/* Opción de ver reglas - solo para tenant_3pl */}
        {isTenant3PL && (
          <DropdownMenuLinkItem
            to={`/clients/${client.id}/rules`}
            onClick={(e) => e.stopPropagation()}
            data-testid="view-rules-action"
          >
            <FontAwesomeIcon icon={faTruck} className="mr-2 w-3.5" />
            {t('actions.viewRules')}
          </DropdownMenuLinkItem>
        )}

        {isTenant3PL && (
          <DropdownMenuLinkItem
            to={`/clients/${client.id}/packaging-rulesets`}
            onClick={(e) => e.stopPropagation()}
            data-testid="view-packaging-rules-action"
          >
            <FontAwesomeIcon icon={faBoxOpen} className="mr-2 w-3.5" />
            {t('actions.viewPackagingRules')}
          </DropdownMenuLinkItem>
        )}

        {/* Opción de impersonar - requiere iam.impersonation.start */}
        {canImpersonate && onImpersonate && (
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleImpersonateClick();
            }}
            data-testid="impersonate-client-action"
          >
            <FontAwesomeIcon icon={faUserSecret} className="mr-2 w-3.5" />
            {t('actions.impersonate')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
      <ImpersonateModal ref={impersonateModalRef} />
    </DropdownMenu>
  );
};
